import { useReducer, useEffect, useRef, useCallback } from 'react';
import { useStore } from 'react-redux';
import { requestStatuses as statuses } from '#helper';

const defaultState = {
  status: statuses.idle,
  options: [],
  error: null
};

const actionTypes = Object.freeze({
  fetchStarted: 'fetchStarted',
  fetchSucceeded: 'fetchSucceeded',
  fetchFailed: 'fetchFailed',
  clear: 'clear'
});

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.fetchStarted:
      return { ...state, status: statuses.loading, error: null };
    case actionTypes.fetchSucceeded:
      return { ...state, status: statuses.succeeded, options: action.payload };
    case actionTypes.fetchFailed:
      return { ...state, status: statuses.failed, options: [], error: action.error };
    case actionTypes.clear:
      return defaultState;
    default:
      return state;
  }
};

const useOptionsService = (searchFields, OptionService, options) => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const isMounted = useRef(true);
  const store = useStore();
  const optionsService = useRef(new OptionService(searchFields, store, options));
  const abortController = useRef(new AbortController());

  useEffect(() => () => {
    abortController.current.abort();
    isMounted.current = false;
  }, []);

  const fetchOptions = useCallback(async (value, debounceTime = 500, ...rest) => {
    try {
      abortController.current.abort();
      dispatch({ type: actionTypes.fetchStarted });
      abortController.current = new AbortController();
      const { signal } = abortController.current;
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, debounceTime);
      });
      const newOptions =
        await optionsService.current?.getOptions(value, { signal }, ...rest);
      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchSucceeded,
          payload: newOptions ?? []
        });
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchFailed,
          error: e
        });
      }
    }
  }, []);

  const clearOptions = useCallback(() => {
    dispatch({
      type: actionTypes.clear
    });
  }, []);

  return { ...state, fetchOptions, clearOptions };
};

export default useOptionsService;
